import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, BookOpen, Sparkles, Info } from "lucide-react";
import { FALLACY_DEFINITIONS, FALLACY_CATEGORIES, type FallacyDefinition } from "@/constants/fallacyDefinitions";
import { getSeverityVariant, getImpactVariant, getCategoryVariant } from "@/utils/fallacyVariants";
import { filterFallacies, type FallacyFilterCriteria } from "@/utils/filtering/fallacyFilters";
import { getAlternativeNamesPreview, getExamplePreview, formatCountText } from "@/utils/data/fallacyHelpers";
import { UI_TEXT, UI_DISPLAY_CONFIG, SEVERITY_OPTIONS } from "@/constants";
import { useCardFlip } from "@/hooks/ui/useCardFlip";
import type { FlippedResponse } from "@/types/flipped";

export const FallacyReference = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("all");
  const [selectedFallacy, setSelectedFallacy] = useState<{ name: string; fallacy: FallacyDefinition; flippedData: FlippedResponse } | null>(null);
  
  const { toggleCardFlip, getFlipState, getFlippedData, isGenerating } = useCardFlip();

  const filteredFallacies = useMemo(() => {
    const criteria: FallacyFilterCriteria = {
      searchTerm,
      category: selectedCategory,
      severity: selectedSeverity
    };
    return filterFallacies(Object.entries(FALLACY_DEFINITIONS), criteria);
  }, [searchTerm, selectedCategory, selectedSeverity]);

  const totalCount = Object.keys(FALLACY_DEFINITIONS).length;
  const showingText = formatCountText(UI_TEXT.format.showingOf, {
    count: filteredFallacies.length,
    total: totalCount
  });


  const handleMoreDetails = (name: string, fallacy: FallacyDefinition, e: React.MouseEvent) => {
    e.stopPropagation();
    const flippedData = getFlippedData(name);
    if (flippedData) {
      setSelectedFallacy({ name, fallacy, flippedData });
    }
  };


  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            {UI_TEXT.headers.fallacyReference}
          </CardTitle>
          <p className="text-slate-600">
            Comprehensive guide to {totalCount} logical fallacies with examples and explanations.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={UI_TEXT.placeholders.searchFallacies}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
              >
                <option value="all">{UI_TEXT.filters.allCategories}</option>
                {Object.entries(FALLACY_CATEGORIES).map(([key, label]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
              >
                <option value="all">{UI_TEXT.filters.allSeverities}</option>
                {SEVERITY_OPTIONS.map((severity) => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-sm text-slate-600">
            {showingText}
          </div>
        </CardContent>
      </Card>

      {/* Fallacy Grid with Flip Animation */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredFallacies.map(([name, fallacy]) => {
          const flipState = getFlipState(name);
          const isLoading = isGenerating && flipState.isFlipped && !flipState.data;

          return (
            <div 
              key={name}
              className={`flip-card ${flipState.isFlipped ? 'flipped' : ''}`}
            >
              <div className="flip-card-inner">
                {/* FRONT FACE - Original Fallacy */}
                <div className="flip-card-front">
                  <Card 
                    className="border-0 shadow-md bg-white/70 backdrop-blur-sm hover:shadow-lg transition-all duration-300 cursor-pointer group h-full flex flex-col"
                    onClick={() => toggleCardFlip(name, fallacy)}
                  >
                    <CardHeader className="space-y-2 pb-4">
                      <div className="flex items-center justify-between">
                        <Badge variant="info">
                          <Sparkles className="w-3 h-3 mr-1" />
                          {UI_TEXT.buttons.flipCard}
                        </Badge>
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-base font-semibold line-clamp-2">
                          {fallacy.name}
                        </CardTitle>
                        <Badge variant={getSeverityVariant(fallacy.severity)} className="shrink-0">
                          {fallacy.severity}
                        </Badge>
                      </div>
                      <Badge variant={getCategoryVariant(fallacy.category)} className="w-fit">
                        {fallacy.category}
                      </Badge>
                      <p className="text-xs text-muted-foreground leading-relaxed pt-3">
                        {fallacy.description}
                      </p>
                    </CardHeader>
                    
                    <CardContent className="flex-1 flex flex-col pt-4">
                      <div className="space-y-1">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {UI_TEXT.tabs.examples}:
                        </h4>
                        {getExamplePreview(fallacy.examples).map((example, index) => (
                          <p key={index} className="text-xs text-muted-foreground italic bg-muted/50 p-2 rounded">
                            {example}
                          </p>
                        ))}
                      </div>

                      {fallacy.alternativeNames && fallacy.alternativeNames.length > 0 && (
                        <div className="space-y-1 mt-auto pt-3">
                          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {UI_TEXT.labels.alsoKnownAs}:
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              const { preview, hasMore, remaining } = getAlternativeNamesPreview(fallacy.alternativeNames);
                              return (
                                <>
                                  {preview.map((altName, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {altName}
                                    </Badge>
                                  ))}
                                  {hasMore && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{remaining}
                                    </Badge>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* BACK FACE - Flipped Constructive Approach */}
                <div className="flip-card-back">
                  <Card 
                    className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 cursor-pointer group h-full flex flex-col"
                    onClick={() => toggleCardFlip(name, fallacy)}
                  >
                    {isLoading ? (
                      <>
                        <CardHeader className="space-y-2 pb-3">
                          <div className="flex items-center justify-between">
                            <Badge variant="constructive">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Constructive Approach
                            </Badge>
                          </div>
                          <div className="flex items-start justify-between gap-2">
                            <Skeleton className="h-6 flex-1" />
                            <Skeleton className="h-5 w-16 shrink-0" />
                          </div>
                          <Skeleton className="h-5 w-24" />
                        </CardHeader>
                        
                        <CardContent className="flex-1 flex flex-col space-y-3">
                          <div className="space-y-2">
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-3/4" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-32" />
                            <Skeleton className="h-16 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-32" />
                            <Skeleton className="h-16 w-full" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-24" />
                            <Skeleton className="h-16 w-full" />
                          </div>
                          <div className="flex gap-2 mt-auto pt-2">
                            <Skeleton className="h-8 flex-1" />
                            <Skeleton className="h-8 w-20" />
                          </div>
                        </CardContent>
                      </>
                    ) : flipState.data ? (
                      <>
                        <CardHeader className="space-y-2 pb-4">
                          <div className="flex items-center justify-between">
                            <Badge variant="constructive">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Constructive Approach
                            </Badge>
                          </div>
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base font-semibold line-clamp-2">
                              {flipState.data.flippedTitle}
                            </CardTitle>
                            <Badge variant={getImpactVariant(flipState.data.flippedSeverity)} className="shrink-0">
                              {flipState.data.flippedSeverity}
                            </Badge>
                          </div>
                          <Badge variant="category-constructive" className="w-fit">
                            {flipState.data.flippedCategory}
                          </Badge>
                          <p className="text-xs text-emerald-900 leading-relaxed pt-3">
                            {flipState.data.flippedDescription}
                          </p>
                        </CardHeader>
                        
                        <CardContent className="flex-1 flex flex-col pt-4">
                          <div className="space-y-4">
                            <div className="space-y-1">
                              <h4 className="text-xs font-medium text-emerald-800 uppercase tracking-wide">
                                Positive Example:
                              </h4>
                              <p className="text-xs text-emerald-800 italic bg-emerald-100/50 p-2 rounded">
                                {flipState.data.flippedExample}
                              </p>
                            </div>

                            <div className="space-y-1">
                              <h4 className="text-xs font-medium text-emerald-900 uppercase tracking-wide">
                                Try This:
                              </h4>
                              <div className="bg-teal-100/50 p-2 rounded border-l-2 border-teal-500">
                                <p className="text-xs text-emerald-900">
                                  {flipState.data.actionableStrategy}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-auto pt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoreDetails(name, fallacy, e);
                              }}
                            >
                              <span className="hidden sm:inline">{UI_TEXT.buttons.moreDetails}</span>
                              <span className="sm:hidden">More</span>
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs h-8 px-3"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCardFlip(name, fallacy);
                              }}
                            >
                              {UI_TEXT.buttons.flipBack}
                            </Button>
                          </div>
                        </CardContent>
                      </>
                    ) : null}
                  </Card>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Insights Modal */}
      <Dialog open={selectedFallacy !== null} onOpenChange={(open) => !open && setSelectedFallacy(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Sparkles className="w-6 h-6 text-emerald-600" />
              {selectedFallacy?.flippedData.flippedTitle}
            </DialogTitle>
            <DialogDescription>
              Deep dive into the constructive counter-strategy
            </DialogDescription>
          </DialogHeader>

          {selectedFallacy && (
            <div className="space-y-6">
              {/* Original Fallacy Info */}
              <Card className="bg-slate-50 border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg text-slate-800">Original Fallacy: {selectedFallacy.fallacy.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Definition</h4>
                    <p className="text-sm text-slate-600">{selectedFallacy.fallacy.description}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">
                      {selectedFallacy.fallacy.examples.length > 1 ? 'Examples' : 'Example'}
                    </h4>
                    {selectedFallacy.fallacy.examples.slice(0, 2).map((example, index) => (
                      <p key={index} className="text-sm text-slate-600 italic mb-2">
                        {selectedFallacy.fallacy.examples.length > 1 && `${index + 1}. `}
                        {example}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed AI Analysis */}
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    Core Weakness
                  </h4>
                  <p className="text-sm text-red-800">{selectedFallacy.flippedData.weakness}</p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    Detailed Flipped Approach
                  </h4>
                  <p className="text-sm text-blue-800">{selectedFallacy.flippedData.flippedApproach}</p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    Comprehensive Mitigation Strategy
                  </h4>
                  <p className="text-sm text-green-800">{selectedFallacy.flippedData.mitigationStrategy}</p>
                </div>

                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r">
                  <h4 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                    Quick Action Phrase
                  </h4>
                  <p className="text-sm text-emerald-800 font-medium">"{selectedFallacy.flippedData.actionableStrategy}"</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {filteredFallacies.length === 0 && (
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Info className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {UI_TEXT.messages.noFallaciesFound}
            </h3>
            <p className="text-slate-600">{UI_TEXT.messages.tryAdjustingFilters}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
